/**
 * Imprimir Certificado de Inversión (Depósito a Plazo Fijo) desde NeoCore.
 *
 * Fetch del PF + asociado + beneficiarios desde Supabase, mapea a los
 * tipos del shared package @neocoop/cert-pdf y abre una ventana con el
 * HTML del certificado lista para imprimir/descargar PDF.
 */
import {
  certificadoPlazoFijoHtml,
  type CertAsociado,
  type CertPlazoFijo,
  type ManejoCapitalizacion,
} from '@neocoop/cert-pdf'
import { supabase } from './supabase'

type Result = { ok: true } | { ok: false; error: string }

export async function imprimirCertificadoPorPlazoFijoId(
  plazoFijoId: string,
): Promise<Result> {
  // 1) Plazo fijo + asociado en un solo fetch
  const { data: pfRow, error: pfErr } = await supabase
    .from('plazo_fijo_detalle')
    .select(
      `id, cuenta_id, verification_code, capital_inicial, tasa_anual, plazo_dias,
       fecha_inicio, fecha_vencimiento, manejo_capitalizacion,
       asociado:asociado_id (id, no_asociado, nombre_completo, dpi)`,
    )
    .eq('id', plazoFijoId)
    .single()

  if (pfErr || !pfRow) {
    return { ok: false, error: pfErr?.message ?? 'Plazo fijo no encontrado' }
  }

  // 2) Beneficiarios — primero busca los del PF (designación específica
  //    por producto, lo que pide IVE). Si está vacío, cae a los del perfil
  //    del asociado (beneficiario.vigente=true) como default.
  const { data: bensPf } = await supabase
    .from('plazo_fijo_beneficiario')
    .select('nombre_completo, porcentaje')
    .eq('plazo_fijo_id', plazoFijoId)
    .order('porcentaje', { ascending: false })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let bensRows: any[] = bensPf ?? []
  let benSource: 'pf' | 'asociado' = 'pf'

  if (bensRows.length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const asocId = (pfRow as any).asociado?.id
    if (asocId) {
      const { data: bensAsoc } = await supabase
        .from('beneficiario')
        .select('nombre_completo, porcentaje')
        .eq('asociado_id', asocId)
        .eq('vigente', true)
        .order('porcentaje', { ascending: false })
      if (bensAsoc && bensAsoc.length > 0) {
        bensRows = bensAsoc
        benSource = 'asociado'
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pf = pfRow as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const asoc = pf.asociado as any

  const num = (v: unknown) => {
    const n = typeof v === 'string' ? parseFloat(v) : (v as number)
    return Number.isFinite(n) ? n : 0
  }

  const capitalInicial = num(pf.capital_inicial)
  const tasaAnual = num(pf.tasa_anual)
  const plazoDias = num(pf.plazo_dias)

  // Rendimiento bruto = capital * tasa * dias / 365 (interés simple, per spec)
  const rendimientoBruto =
    Math.round(((capitalInicial * tasaAnual * plazoDias) / 365) * 100) / 100

  const cAsociado: CertAsociado | null = asoc
    ? {
        nombreCompleto: String(asoc.nombre_completo ?? ''),
        noAsociado: String(asoc.no_asociado ?? ''),
        dpi: String(asoc.dpi ?? ''),
      }
    : null

  const cPf: CertPlazoFijo = {
    cuentaId: String(pf.cuenta_id),
    verificationCode: String(pf.verification_code ?? ''),
    capitalInicial,
    tasaAnual,
    plazoDias,
    fechaInicio: String(pf.fecha_inicio),
    fechaVencimiento: String(pf.fecha_vencimiento),
    manejoCapitalizacion:
      pf.manejo_capitalizacion === 'AL_VENCIMIENTO'
        ? ('AL_VENCIMIENTO' as ManejoCapitalizacion)
        : ('MENSUAL' as ManejoCapitalizacion),
    rendimientoBruto,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beneficiarios: bensRows.map((b: any) => ({
      nombreCompleto: String(b.nombre_completo),
      porcentaje: num(b.porcentaje),
    })),
    beneficiariosSource: benSource,
  }

  const html = certificadoPlazoFijoHtml(cAsociado, cPf)

  // 3) Abrir ventana nueva con el HTML (Letter landscape adentro) y disparar
  //    print. Mismo patrón que imprimibles.ts.
  const w = window.open('', '_blank', 'width=1280,height=860')
  if (!w) {
    return {
      ok: false,
      error: 'No se pudo abrir la ventana. Revisá si el browser bloqueó el popup.',
    }
  }
  w.document.write(html)
  w.document.close()
  // Esperamos que las fuentes carguen antes de imprimir.
  w.onload = () => {
    setTimeout(() => {
      try {
        w.focus()
        w.print()
      } catch {
        // Si falla print(), igual queda la ventana abierta para que el operador
        // dispare Ctrl+P manualmente.
      }
    }, 500)
  }

  return { ok: true }
}
